import { colors } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { pxToRem } from '@creditornot/cb-utils';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Campaign, CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';

import { MAXIMUM_TIMESPAN } from './hooks/constants';
import { useChartState } from './hooks/useChartState';
import { useCampaignModuleContext } from '../../../context';
import { useSingleCampaignPerformance } from '../../../hooks/useSingleCampaignPerformance';
import { AdCampaign, CampaignStatus } from '../../../types';
import { getCampaignStatus } from '../../../utils/getCampaignStatus';
import { useTelemetryCampaignPerformanceView } from '../../../utils/telemetry/telemetry';
import { AnalyticsChart } from '../../analytics-chart/AnalyticsChart';
import { CampaignAnalyticsMetrics } from '../CampaignAnalyticsMetrics';
import { CampaignDateRange } from '../CampaignDateRange';
import { EmptyCampaignAnalyticsState } from '../EmptyCampaignAnalyticsState';

type CampaignPerformanceProps = { campaign: Campaign | AdCampaign };

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 24px 16px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SalesValue = styled.span`
  color: ${colors.lime100};
`;

const StyledEmptyCampaignAnalyticsState = styled(EmptyCampaignAnalyticsState)`
  margin-block-start: ${pxToRem(180)};

  @media (max-width: ${MOBILE_BREAKPOINT + 1}px) {
    margin-block-start: ${pxToRem(80)};
  }
`;

const LoadingRoot = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
`;

const getCampaignImpactEndDateOrStopDate = (campaign: Campaign | AdCampaign): DateTime => {
  if (
    campaign.campaign_type === CampaignType.ADS &&
    campaign.status === CampaignStatus.STOPPED &&
    campaign.latest_stopped_at
  ) {
    return DateTime.fromISO(campaign.latest_stopped_at);
  }

  const campaignImpactEndDateOrStopDate =
    campaign.end_date &&
    DateTime.fromISO(campaign.end_date)
      .endOf('day')
      .plus(campaign.campaign_type === CampaignType.ADS ? { days: 7 } : 0); // ads campaigns calculate impact for 7 days after campaign end

  const todayDate = DateTime.now().endOf('day');
  return campaignImpactEndDateOrStopDate ? DateTime.min(campaignImpactEndDateOrStopDate, todayDate) : todayDate;
};

export const CampaignPerformance = ({ campaign }: CampaignPerformanceProps) => {
  const { currency, entityType, venues, merchantId } = useCampaignModuleContext();
  const { locale } = useIntl();
  const venueId = venues[0]?.id;
  const roasMonetaryBase = convertMonetaryIntToDecimal(100, currency); // we'll show 1.00 EUR, but 100 JPY instead of 1 JPY to save precision for ROAS value

  const campaignStartDate = DateTime.fromISO(campaign.start_date).startOf('day');
  const maximumFromDate = DateTime.now().minus(MAXIMUM_TIMESPAN).startOf('day');
  const dateRange = {
    from: DateTime.max(campaignStartDate, maximumFromDate),
    to: getCampaignImpactEndDateOrStopDate(campaign),
  };
  const formattedDateRangeFrom = dateRange.from.toLocaleString(DateTime.DATE_SHORT, { locale });
  const formattedDateRangeTo = dateRange.to.toLocaleString(DateTime.DATE_SHORT, { locale });

  const { campaignPerformance, isFetching } = useSingleCampaignPerformance({
    entityType,
    entityId: entityType === 'venue' ? venueId : merchantId,
    campaign: { campaignId: campaign?.id ?? '', campaignType: campaign?.campaign_type ?? '' },
    dateRange,
    enabled: true,
  });

  useTelemetryCampaignPerformanceView(campaign.campaign_type);

  const {
    totalOrders,
    avgBasketValue,
    avgBasketSize,
    totalSpend,
    returningCustomers,
    newCustomers,
    totalSales,
    avgCostPerOrder,
    percentageCoveredByWolt,
    totalMerchantSpend,
    totalWoltSpend,
  } = campaignPerformance ?? {};

  const { category, chartData, isLoading, onChangeCategory, onGoBack, onGoForward } = useChartState({
    entityType,
    entityId: entityType === 'venue' ? venueId : merchantId,
    campaign,
  });

  if (isFetching) {
    return (
      <LoadingRoot>
        <Spinner size="large" />
      </LoadingRoot>
    );
  }

  const todayDate = DateTime.now().endOf('day');
  if (campaignStartDate > todayDate) {
    const startDate = campaignStartDate.toLocaleString(DateTime.DATE_MED, { locale });
    return <StyledEmptyCampaignAnalyticsState startDate={startDate} />;
  }

  const campaignStatus = campaign.campaign_type === CampaignType.ADS ? campaign.status : getCampaignStatus(campaign);
  const isCampaignFinished = campaignStatus !== CampaignStatus.RUNNING && campaignStatus !== CampaignStatus.SCHEDULED;

  return (
    <Root>
      <HeaderRow>
        <T.Heading6>
          {!!totalSales && !!totalSpend ? (
            <FormattedMessage
              id={
                isCampaignFinished ? 'campaigns.analytics-roas-summary-completed' : 'campaigns.analytics-roas-summary'
              }
              values={{
                sales: (
                  <SalesValue>
                    <MonetaryAmount amount={(totalSales / totalSpend) * roasMonetaryBase} currency={currency} />
                  </SalesValue>
                ),
                spent: <MonetaryAmount amount={roasMonetaryBase} currency={currency} />,
              }}
            />
          ) : (
            <FormattedMessage
              id={isCampaignFinished ? 'campaigns.analytics.no-sales' : 'campaigns.analytics.no-sales-yet'}
            />
          )}
        </T.Heading6>
      </HeaderRow>
      <Content>
        <CampaignAnalyticsMetrics
          currency={currency}
          metrics={{
            totalSales: totalSales ?? 0,
            totalSpend: totalSpend ?? 0,
            totalOrders: totalOrders ?? 0,
            averageBasketValue: avgBasketValue ?? 0,
            averageBasketItemsCount: avgBasketSize ?? 0,
            averageCostPerOrder: percentageCoveredByWolt && percentageCoveredByWolt === 100 ? 0 : avgCostPerOrder ?? 0,
            ...(entityType === 'venue' || newCustomers != null ? { newCustomers: newCustomers ?? 0 } : {}),
            ...(entityType === 'venue' || returningCustomers != null
              ? { returningCustomers: returningCustomers ?? 0 }
              : {}),
            totalMerchantSpend,
            totalWoltSpend,
          }}
          isLoading={false}
          colored={false}
          isSingleCampaign={false}
        />
        <AnalyticsChart
          currency={currency}
          category={category}
          isLoading={isLoading}
          rawChartData={chartData}
          onGoBack={onGoBack}
          onGoForward={onGoForward}
          onChangeCategory={onChangeCategory}
        />
      </Content>
      <CampaignDateRange startDateTime={formattedDateRangeFrom} endDateTime={formattedDateRangeTo} />
    </Root>
  );
};
